import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Check, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useJournal, JournalEntry } from '@/hooks/useJournal';
import { getPromptsForDay, moodOptions } from '@/data/journalPrompts';
import { format } from 'date-fns';

interface ReflectionWorksheetProps {
  currentDay: number;
  ramadanStartDate?: string;
}

export const ReflectionWorksheet = ({ currentDay, ramadanStartDate }: ReflectionWorksheetProps) => {
  const { getEntryByDate, saveEntry, isLoading } = useJournal();
  const prompts = getPromptsForDay(currentDay);
  
  // Calculate the actual date for this day
  const getDateForDay = (day: number): string => {
    if (ramadanStartDate) {
      const start = new Date(ramadanStartDate);
      start.setDate(start.getDate() + day - 1);
      return format(start, 'yyyy-MM-dd');
    }
    // Fallback: use current date
    return format(new Date(), 'yyyy-MM-dd');
  };

  const dateKey = getDateForDay(currentDay);
  const existingEntry = getEntryByDate(dateKey);

  const [mood, setMood] = useState(existingEntry?.mood || '');
  const [gratitude, setGratitude] = useState(existingEntry?.gratitude || '');
  const [reflection, setReflection] = useState(existingEntry?.reflection || '');
  const [needsFromAllah, setNeedsFromAllah] = useState(existingEntry?.needsFromAllah || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isEditing, setIsEditing] = useState(!existingEntry);

  // Update form when day changes or entry loads
  useEffect(() => {
    const entry = getEntryByDate(dateKey);
    setMood(entry?.mood || '');
    setGratitude(entry?.gratitude || '');
    setReflection(entry?.reflection || '');
    setNeedsFromAllah(entry?.needsFromAllah || '');
    setIsEditing(!entry);
    setSaved(false);
  }, [dateKey, getEntryByDate]);

  const handleSave = async () => {
    if (!mood && !gratitude && !reflection && !needsFromAllah) return;
    
    setIsSaving(true);
    await saveEntry({
      id: existingEntry?.id,
      date: dateKey,
      mood,
      gratitude,
      reflection,
      needsFromAllah
    });
    setIsSaving(false);
    setSaved(true);
    setIsEditing(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const hasContent = mood || gratitude || reflection || needsFromAllah;
  const hasChanges = 
    mood !== (existingEntry?.mood || '') ||
    gratitude !== (existingEntry?.gratitude || '') ||
    reflection !== (existingEntry?.reflection || '') ||
    needsFromAllah !== (existingEntry?.needsFromAllah || '');

  if (isLoading) {
    return (
      <div className="bg-cream rounded-sm p-6 text-center">
        <p className="text-muted-foreground">Loading your reflections...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
    >
      {/* View mode for existing entries */}
      {existingEntry && !isEditing ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Saved on {format(new Date(dateKey), 'MMMM d, yyyy')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="gap-2"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </Button>
          </div>

          {existingEntry.mood && (
            <div className="bg-cream rounded-sm p-4">
              <p className="text-xs text-muted-foreground mb-2">Mood</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl">
                  {moodOptions.find(m => m.value === existingEntry.mood)?.emoji || '📝'}
                </span>
                <span className="text-foreground capitalize">{existingEntry.mood}</span>
              </div>
            </div>
          )}

          {existingEntry.gratitude && (
            <div className="space-y-2">
              <p className="text-xs text-gold uppercase tracking-wider">Gratitude</p>
              <p className="text-foreground/85 leading-relaxed bg-cream p-4 rounded-sm">
                {existingEntry.gratitude}
              </p>
            </div>
          )}

          {existingEntry.reflection && (
            <div className="space-y-2">
              <p className="text-xs text-gold uppercase tracking-wider">Reflection</p>
              <p className="text-foreground/85 leading-relaxed bg-cream p-4 rounded-sm">
                {existingEntry.reflection}
              </p>
            </div>
          )}

          {existingEntry.needsFromAllah && (
            <div className="space-y-2">
              <p className="text-xs text-gold uppercase tracking-wider">Needs from Allah</p>
              <p className="text-foreground/85 leading-relaxed bg-cream p-4 rounded-sm">
                {existingEntry.needsFromAllah}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Edit mode */
        <div className="space-y-8">
          {/* Mood Selection */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90">
              <span className="text-gold mr-2">1.</span>
              {prompts.moodQuestion}
            </p>
            <div className="flex flex-wrap gap-2">
              {moodOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setMood(option.value)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all ${
                    mood === option.value
                      ? 'bg-gold text-white'
                      : 'bg-cream hover:bg-cream/80 text-foreground/80'
                  }`}
                >
                  <span>{option.emoji}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Gratitude */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90">
              <span className="text-gold mr-2">2.</span>
              {prompts.gratitudePrompt}
            </p>
            <Textarea
              value={gratitude}
              onChange={(e) => setGratitude(e.target.value)}
              placeholder="Write your gratitude here..."
              className="min-h-[100px] bg-cream/50 border-dashed focus:border-gold/50"
            />
          </div>

          {/* Reflection */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90">
              <span className="text-gold mr-2">3.</span>
              {prompts.reflectionPrompt}
            </p>
            <Textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="Share your thoughts..."
              className="min-h-[100px] bg-cream/50 border-dashed focus:border-gold/50"
            />
          </div>

          {/* Needs from Allah */}
          <div className="space-y-3">
            <p className="text-sm text-foreground/90">
              <span className="text-gold mr-2">4.</span>
              {prompts.duaPrompt}
            </p>
            <Textarea
              value={needsFromAllah}
              onChange={(e) => setNeedsFromAllah(e.target.value)}
              placeholder="Pour out your heart to Allah..."
              className="min-h-[100px] bg-cream/50 border-dashed focus:border-gold/50"
            />
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-between pt-4">
            {existingEntry && (
              <Button
                variant="ghost"
                onClick={() => {
                  setMood(existingEntry.mood || '');
                  setGratitude(existingEntry.gratitude || '');
                  setReflection(existingEntry.reflection || '');
                  setNeedsFromAllah(existingEntry.needsFromAllah || '');
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasContent || isSaving || (!hasChanges && !!existingEntry)}
              className={`gap-2 ${existingEntry ? '' : 'ml-auto'} ${
                saved ? 'bg-emerald-600 hover:bg-emerald-600' : ''
              }`}
            >
              {saved ? (
                <>
                  <Check className="w-4 h-4" />
                  Saved
                </>
              ) : isSaving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {existingEntry ? 'Update' : 'Save'} Reflection
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};
